import { jwtDecode } from 'jwt-decode';
import defaultConfigs from '../components/defaultConfigs.json';
import { fetchGroups } from '../services/GroupsApi';
import { findUsersByQuery } from '../services/UserApi';

const billingConfigDefault = defaultConfigs.billingConfig;

/**
 * Checks for a valid, non-expired B2C access token in local storage.
 * @returns {boolean} True if a valid token exists, false otherwise.
 */
export function hasLoggedInUser() {
    const accessToken = localStorage.getItem('b2c_access_token');
    const expiresAt = localStorage.getItem('b2c_token_expires_at');

    if (!accessToken || !expiresAt) {
        return false;
    }

    const expirationTime = parseInt(expiresAt, 10);
    const now = Date.now();

    return now < expirationTime;
}

/**
 * Decodes the B2C ID token from local storage to get user information.
 * @returns {{id: string, email: string} | null} User object or null if not logged in.
 */
export function getCurrentUser() {
    const accessToken = localStorage.getItem('b2c_access_token');
    if (!accessToken) {
        return null;
    }

    try {
        const decoded = jwtDecode(accessToken);
        const extensionKey = Object.keys(decoded).find(key => key.includes('mongoUserId'));
        return {
            id: extensionKey ? decoded[extensionKey] : decoded.oid || decoded.sub,
            email: decoded.emails?.[0],
        };
    } catch (error) {
        console.error('Failed to decode B2C ID token:', error);
        return null;
    }
}

/**
 * Fetches the full user profile and workspace data from the backend.
 * @param {string} [id] - Optional user ID. If not provided, it's retrieved from the current user's token.
 * @returns {Object | null} The user's custom data object or null.
 */
export const getCustomData = async (id) => {

    let queryResult = await findUsersByQuery({ id });

    let customData = queryResult?.users?.[0] || null;
    if (!customData) {
        console.warn('User not found in database. User may need to complete registration.');
        return null;
    }

    let group_ids = customData.workspaces ? customData.workspaces.map(workspace => workspace.group_id) : [];
    if (!group_ids.includes(customData.group_id)) {
        group_ids.push(customData.group_id);
    }

    const { groups: groupsList } = await fetchGroups({
        group_id: { $in: group_ids },
    });

    let groupData = groupsList.find(group => group.group_id === customData.group_id);

    groupsList.sort((a, b) => {
        let indexA = group_ids.indexOf(a.group_id);
        let indexB = group_ids.indexOf(b.group_id);
        return indexA - indexB;
    });

    customData.workspaces = groupsList;

    groupData = {
        billingConfig: { ...billingConfigDefault },
        ...groupData,
    };

    const data = { ...groupData, ...customData };

    return data;
};

export async function getB2CToken() {
    const accessToken = localStorage.getItem('b2c_access_token');
    const expiresAt = localStorage.getItem('b2c_token_expires_at');
    const refreshToken = localStorage.getItem('b2c_refresh_token');

    if (!accessToken) return null;

    // Check if token is expired or expiring soon (within 5 minutes)
    const now = Date.now();
    const expirationTime = parseInt(expiresAt, 10);
    const bufferTime = 5 * 60 * 1000; // 5 minutes

    if (expirationTime && now >= expirationTime - bufferTime && refreshToken) {
        // Token is expired or expiring soon, try to refresh
        try {
            const { refreshB2CToken } = await import('../services/AuthApi');
            const tokens = await refreshB2CToken(refreshToken);

            // Update stored tokens
            localStorage.setItem('b2c_access_token', tokens.accessToken);
            if (tokens.refreshToken) {
                localStorage.setItem('b2c_refresh_token', tokens.refreshToken);
            }
            const newExpiresAt = Date.now() + (tokens.expiresIn * 1000);
            localStorage.setItem('b2c_token_expires_at', newExpiresAt.toString());

            return tokens.accessToken;
        } catch (error) {
            console.error('Failed to refresh B2C token:', error);
            // Clear invalid tokens
            localStorage.removeItem('b2c_access_token');
            localStorage.removeItem('b2c_refresh_token');
            localStorage.removeItem('b2c_token_expires_at');
            return null;
        }
    }

    return accessToken;
}